import {
    Badge,
    Body1,
    Body1Strong,
    Button,
    Card,
    Label,
    makeStyles,
    MessageBar,
    Table,
    TableBody,
    TableCell,
    TableCellActions,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Textarea,
    Title3,
    tokens
} from "@fluentui/react-components";
import {AddCircle24Regular, ArrowLeft24Regular, Delete24Regular} from "@fluentui/react-icons";
import React, {useState} from "react";
import {errorAtom, groupsTabViewAtom} from "../../../store.ts";
import {useSetAtom} from "jotai";
import {useTranslation} from "react-i18next";
import {ConfirmAudienceModal} from "../teams/ConfirmModal.tsx";

interface ManualUser {
    id: string;
    displayName: string;
    userPrincipalName: string;
    assignedTeam: {id: string};
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        '@media (max-width: 640px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
        marginBottom: '24px',
    },
    textareaWrapper: {
        position: 'relative',
        width: '100%',
    },
    textarea: {
        minHeight: '150px',
        fontFamily: 'monospace',
        width: '100%',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '12px',
        marginTop: '16px',
    },
    selectedUsersSection: {
        marginTop: '24px',
    },
    noResults: {
        textAlign: 'center',
        padding: '16px',
        color: tokens.colorNeutralForeground3,
    },
    countBadge: {
        marginLeft: '8px',
    },
    responsiveTable: {
        '@media (max-width: 640px)': {
            '& table': {
                display: 'block',
                width: '100%',
            },
            '& thead': {
                display: 'none',
            },
            '& tbody': {
                display: 'block',
                width: '100%',
            },
            '& tr': {
                display: 'flex',
                flexDirection: 'column',
                padding: '12px 0',
                marginBottom: '8px',
                '&:not(:last-child)': {
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    paddingBottom: '16px',
                },
            },
            '& td': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: 'none',
                padding: '4px 0',
                width: '100%',
                position: 'relative',
                '&::before': {
                    content: 'attr(data-label)',
                    fontWeight: 'bold',
                    marginRight: '8px',
                },
                '&:last-child': {
                    justifyContent: 'flex-end',
                    marginTop: '8px',
                },
            },
            '& [role="cell-actions"]': {
                opacity: '1 !important',
                transform: 'none !important',
                padding: '0 !important',
            },
        }
    }
});

export const AddAudienceManualView: React.FC = () => {
    const styles = useStyles();
    const {t} = useTranslation();
    const setError = useSetAtom(errorAtom);
    const setGroupView = useSetAtom(groupsTabViewAtom);

    const [userIdsInput, setUserIdsInput] = useState<string>('');
    const [selectedUsers, setSelectedUsers] = useState<ManualUser[]>([]);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

    const handleProcessUserIds = () => {
        if (!userIdsInput.trim()) {
            setError(t("error.no-user-ids-entered"));
            return;
        }

        const lines = userIdsInput.split('\n');
        const newUsers: ManualUser[] = [];
        const existingIds = new Set(selectedUsers.map(user => user.id));

        lines.forEach(line => {
            const trimmedLine = line.trim();
            if (!trimmedLine) return; // Skip empty lines

            let userId: string;

            if (trimmedLine.includes(',')) {
                const parts = trimmedLine.split(',').map(part => part.trim());
                userId = parts[0];
            } else {
                userId = trimmedLine;
            }

            if (userId && !existingIds.has(userId)) {
                newUsers.push({
                    id: userId,
                    displayName: t("text.user-id", {id: userId}),
                    userPrincipalName: userId,
                    assignedTeam: {id: 'manual'}
                });
                existingIds.add(userId);
            }
        });

        if (newUsers.length === 0) {
            setError(t("error.no-new-valid-user-ids"));
            return;
        }

        setSelectedUsers(prevUsers => [...prevUsers, ...newUsers]);
        setUserIdsInput(''); // Clear the input after processing
    };

    const handleRemoveUser = (userId: string) => {
        setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
    };

    const handleBack = () => {
        setGroupView('list');
    };

    const handleCreateAudience = () => {
        if (selectedUsers.length > 0) {
            setShowConfirmModal(true);
        } else {
            setError(t("error.no-audience-participants"));
        }
    };

    // Convert ManualUser to Participant format needed by ConfirmAudienceModal
    const selectedParticipants = selectedUsers.map(user => ({
        id: user.id,
    }));

    // Handle post-success actions
    const handlePostSuccess = () => {
        setSelectedUsers([]);
        setUserIdsInput('');
        setShowConfirmModal(false);
        setGroupView('list');
    };

    return (
        <Card>
            <div className={styles.container}>
                <div className={styles.header}>
                    <Title3>{t("text.add-users-manually")}</Title3>
                </div>

                <MessageBar intent="info">
                    <Body1>
                        <span dangerouslySetInnerHTML={{__html: t("message.enter-user-ids-one-per-line")}}></span>
                    </Body1>
                </MessageBar>

                <div className={styles.inputContainer}>
                    <Label htmlFor="userIdsInput">{t("text.enter-user-ids")}</Label>
                    <div className={styles.textareaWrapper}>
                        <Textarea
                            id="userIdsInput"
                            className={styles.textarea}
                            placeholder={t("placeholder.enter-user-ids-format")}
                            value={userIdsInput}
                            rows={10}
                            onChange={(e) => setUserIdsInput(e.target.value)}
                            resize="vertical"
                        />
                    </div>
                    <Button
                        appearance="primary"
                        disabled={!userIdsInput.trim()}
                        onClick={handleProcessUserIds}
                    >
                        {t("button.add-users")}
                    </Button>
                </div>

                <div className={styles.selectedUsersSection}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px'
                    }}>
                        <Body1Strong>
                            {t("text.selected-users")}
                            <Badge
                                appearance="filled"
                                color={selectedUsers.length > 0 ? "success" : "warning"}
                                className={styles.countBadge}
                            >
                                {selectedUsers.length}
                            </Badge>
                        </Body1Strong>

                        {selectedUsers.length > 0 && (
                            <Button
                                appearance="subtle"
                                size="small"
                                onClick={() => setSelectedUsers([])}
                            >
                                {t("button.clear")}
                            </Button>
                        )}
                    </div>

                    {selectedUsers.length === 0 ? (
                        <div className={styles.noResults}>
                            {t("text.no-users-selected")}
                        </div>
                    ) : (
                        <div style={{overflowX: 'auto'}} className={styles.responsiveTable}>
                            <Table aria-label="Selected users table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>{t("table.header.user-id")}</TableHeaderCell>
                                        <TableHeaderCell>{t("table.header.action")}</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {selectedUsers.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell data-label={t("table.header.user-id")}>
                                                {user.id}
                                            </TableCell>
                                            <TableCell data-label={t("table.header.action")}>
                                                <TableCellActions>
                                                    <Button
                                                        icon={<Delete24Regular/>}
                                                        onClick={() => handleRemoveUser(user.id)}
                                                        aria-label={t("button.remove")}
                                                    >
                                                        {t("button.delete")}
                                                    </Button>
                                                </TableCellActions>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                <div className={styles.buttonContainer}>
                    <Button
                        appearance="secondary"
                        icon={<ArrowLeft24Regular/>}
                        onClick={handleBack}
                    >
                        {t("button.back-to-audiences-list")}
                    </Button>
                    <Button
                        appearance="primary"
                        icon={<AddCircle24Regular/>}
                        disabled={selectedUsers.length === 0}
                        onClick={handleCreateAudience}
                    >
                        {t("button.create-audience")}
                    </Button>
                </div>
            </div>

            {showConfirmModal && (
                <ConfirmAudienceModal
                    selectedParticipants={selectedParticipants}
                    closeModal={() => setShowConfirmModal(false)}
                    postSuccess={handlePostSuccess}
                />
            )}
        </Card>
    );
}
